import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import CopyButton from "../components/CopyButton";
import BackButton from "../components/BackButton";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { normalizeMarkdown } from "../utils/normalizeMarkdown";
import { useTranslation } from "react-i18next";
import {
  Beaker, FlaskConical, MessageSquare, FileText, Database, LogOut, Upload,
  Trash2, Search, Loader2, FileUp, Brain, Shield, Star, Send, Sparkles, Globe
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const categories = [
  { value: "general", label: "General" },
  { value: "research", label: "Research Papers" },
  { value: "datasheet", label: "Technical Datasheets" },
  { value: "formulation", label: "Formulation Guides" },
  { value: "testing", label: "Testing Reports" },
];

export default function DocumentsPage() {
  const { user, logout } = useAuth();
  const { i18n } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadFileName, setUploadFileName] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [category, setCategory] = useState("general");
  const [tags, setTags] = useState("");
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [favOnly, setFavOnly] = useState(false);

  // Document chat state
  const [selected, setSelected] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [docMode, setDocMode] = useState("strict");
  const [docMsgs, setDocMsgs] = useState([]);
  const [docMsgsLoading, setDocMsgsLoading] = useState(false);
  const [docInput, setDocInput] = useState("");
  const [docLoading, setDocLoading] = useState(false);

  useEffect(() => { fetchDocs(); }, []);

  const fetchDocs = async () => {
    try { const r = await axios.get(`${API}/documents`); setDocs(r.data); } catch (e) {}
    finally { setLoading(false); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadPct(0);
    setUploadFileName(f.name);
    const fd = new FormData();
    fd.append("file", f); fd.append("category", category); fd.append("tags", tags);
    try {
      await axios.post(`${API}/upload-document`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setUploadOpen(false); setTags("");
      fetchDocs();
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadPct(0);
      setUploadFileName("");
      e.target.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
    } catch (e) {}
  };

  const toggleFav = async (d) => {
    try {
      const r = await axios.patch(`${API}/documents/${d.id}/favorite`);
      setDocs((prev) => prev.map((x) => (x.id === d.id ? { ...x, is_favorite: r.data.is_favorite } : x)));
    } catch (e) {}
  };

  const openChat = async (d) => {
    setSelected(d);
    setChatOpen(true);
    setDocInput("");
    setDocMsgs([]);
    setDocMsgsLoading(true);
    try {
      const r = await axios.get(`${API}/documents/${d.id}/chat`);
      setDocMsgs(r.data.messages || []);
    } catch (e) {}
    finally { setDocMsgsLoading(false); }
  };

  const sendDocMsg = async () => {
    if (!docInput.trim() || !selected || docLoading) return;
    const msg = docInput.trim();
    setDocInput("");
    setDocMsgs((m) => [...m, { role: "user", content: msg, mode: docMode }]);
    setDocLoading(true);
    try {
      const r = await axios.post(`${API}/documents/${selected.id}/chat`, {
        message: msg,
        mode: docMode,
        language: (i18n.language || "en").startsWith("de") ? "de" : "en",
      });
      setDocMsgs((m) => [...m, { role: "assistant", content: r.data.response, mode: docMode }]);
    } catch (e) {
      setDocMsgs((m) => [...m, { role: "assistant", content: "Error — please retry." }]);
    } finally {
      setDocLoading(false);
    }
  };

  const filtered = docs.filter((d) =>
    (filterCat === "all" || d.category === filterCat) &&
    (!favOnly || d.is_favorite) &&
    (!search || d.filename.toLowerCase().includes(search.toLowerCase()) || d.summary?.toLowerCase().includes(search.toLowerCase()))
  );

  const navItems = [
    { icon: MessageSquare, label: "Chat", path: "/chat" },
    { icon: FlaskConical, label: "Formulation", path: "/formulation" },
    { icon: FileText, label: "Documents", path: "/documents" },
    { icon: Database, label: "Knowledge", path: "/knowledge" },
    { icon: Brain, label: "Training", path: "/training" },
    ...(user?.is_admin ? [{ icon: Shield, label: "Admin", path: "/admin" }] : []),
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="w-72 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-orange-500 flex items-center justify-center"><Beaker className="w-5 h-5 text-white" /></div>
            <span className="font-heading font-bold text-lg text-slate-900">ElastoAI</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${item.path === "/documents" ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
              <item.icon className="w-5 h-5" /><span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center"><span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0).toUpperCase()}</span></div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p></div>
          <button onClick={logout} className="p-2 hover:bg-slate-100 rounded-lg"><LogOut className="w-4 h-4 text-slate-500" /></button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <BackButton />
            <FileText className="w-5 h-5 text-orange-500" />
            <h1 className="font-heading font-semibold text-slate-900">Documents</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-doc-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"><Upload className="w-4 h-4 mr-2" /> Upload</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle>Upload Document (PDF / Excel / Image)</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="e.g., NBR, datasheet, 70 shore" data-testid="doc-tags-input" />
                </div>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                  <input type="file" accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.txt,.csv" id="file-up" className="hidden" onChange={upload} disabled={uploading} data-testid="file-input" />
                  <label htmlFor="file-up" className={`cursor-pointer ${uploading ? "pointer-events-none" : ""}`}>
                    {uploading ? <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto" /> : <FileUp className="w-10 h-10 text-slate-400 mx-auto mb-2" />}
                    <p className="text-slate-600 mt-2">
                      {uploading ? (uploadPct < 100 ? `Uploading ${uploadFileName} — ${uploadPct}%` : "Extracting text & generating AI summary...") : "Click to upload"}
                    </p>
                    {uploading && (
                      <div className="mt-3 max-w-xs mx-auto">
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-orange-500 rounded-full transition-all duration-200" style={{ width: `${uploadPct < 100 ? uploadPct : 100}%` }} />
                        </div>
                        <p className="text-xs text-slate-500 mt-2">This may take up to a minute for large PDFs.</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents..." className="pl-10 h-11 bg-white" data-testid="doc-search" />
              </div>
              <Select value={filterCat} onValueChange={setFilterCat}>
                <SelectTrigger className="w-48 h-11 bg-white" data-testid="doc-filter-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={() => setFavOnly((v) => !v)} data-testid="doc-fav-filter"
                title="Show favorites only"
                className={`h-11 px-4 rounded-lg border flex items-center gap-2 text-sm font-medium ${favOnly ? "bg-amber-50 border-amber-300 text-amber-700" : "bg-white border-slate-200 text-slate-600"}`}>
                <Star className={`w-4 h-4 ${favOnly ? "text-amber-500 fill-amber-500" : "text-slate-400"}`} /> Favorites
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">{favOnly ? "No favorite documents yet" : "No documents"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((d) => (
                  <div key={d.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`document-${d.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-red-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-900">{d.filename}</h3>
                          <button onClick={() => toggleFav(d)} data-testid={`doc-fav-${d.id}`}
                            title={d.is_favorite ? "Remove from favorites" : "Mark as favorite"} className="p-1 rounded hover:bg-amber-50">
                            <Star className={`w-4 h-4 ${d.is_favorite ? "text-amber-500 fill-amber-500" : "text-slate-300"}`} />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mb-2">
                          {d.category} • {Math.round((d.size_bytes || 0) / 1024)} KB • {new Date(d.created_at).toLocaleDateString()}
                        </p>
                        {d.summary && <p className="text-sm text-slate-600 line-clamp-3">{d.summary}</p>}
                        {d.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {d.tags.map((t) => <span key={t} className="text-xs px-2 py-0.5 bg-slate-100 rounded-full text-slate-600">#{t}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button variant="outline" size="sm" onClick={() => openChat(d)} data-testid={`analyze-${d.id}`}>
                          <MessageSquare className="w-4 h-4 mr-1" /> Chat / Analyze
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => del(d.id)} className="text-red-500" data-testid={`delete-doc-${d.id}`}>
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Document chat dialog — continuous conversation, strict/hybrid, full history */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="sm:max-w-3xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-8">
              <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />
              <span className="truncate">{selected?.filename}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
              <button onClick={() => setDocMode("strict")} data-testid="doc-mode-strict"
                title="Answers ONLY from this document"
                className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${docMode === "strict" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}>
                <Shield className="w-3 h-3" /> Strict — document only
              </button>
              <button onClick={() => setDocMode("hybrid")} data-testid="doc-mode-hybrid"
                title="Document first, plus expert polymer knowledge"
                className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${docMode === "hybrid" ? "bg-white shadow text-slate-900" : "text-slate-600"}`}>
                <Sparkles className="w-3 h-3" /> Hybrid — doc + expertise
              </button>
            </div>
            <span className="text-[11px] text-slate-400">
              {docMode === "strict" ? "Out-of-document questions will be refused" : "Answers may add polymer expertise beyond the document"}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/60" data-testid="doc-chat-messages">
            {docMsgsLoading ? (
              <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
            ) : docMsgs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 text-sm">
                <Globe className="w-8 h-8 mb-2 text-slate-300" />
                Ask anything about this document — the full conversation is saved and shown here every time.
              </div>
            ) : (
              docMsgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} group`}>
                  <div className={`px-3 py-2 rounded-lg text-sm ${m.role === "user" ? "bg-slate-900 text-white max-w-[85%]" : "bg-white border border-slate-200 text-slate-900 max-w-[95%] w-full"}`}>
                    {m.role === "assistant" ? (
                      <div className="prose prose-sm max-w-none break-words prose-p:my-2">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}
                          components={{
                            table: (p) => <div className="overflow-x-auto my-2"><table className="w-full border-collapse text-xs" {...p} /></div>,
                            th: (p) => <th className="border border-slate-300 px-2 py-1 bg-slate-100 text-left font-semibold" {...p} />,
                            td: (p) => <td className="border border-slate-300 px-2 py-1 align-top" {...p} />,
                          }}>
                          {normalizeMarkdown(m.content)}
                        </ReactMarkdown>
                        <div className="mt-1 opacity-0 group-hover:opacity-100 not-prose"><CopyButton text={m.content} /></div>
                      </div>
                    ) : (
                      <div>{m.content}</div>
                    )}
                  </div>
                </div>
              ))
            )}
            {docLoading && (
              <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Analyzing document...</div>
            )}
          </div>

          <div className="flex gap-2 items-end flex-shrink-0">
            <Textarea
              value={docInput}
              onChange={(e) => setDocInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendDocMsg(); } }}
              placeholder="Ask a question about this document... (Enter to send)"
              rows={1}
              className="bg-white border-slate-200 resize-none min-h-[44px] max-h-28 py-2.5"
              data-testid="doc-chat-input"
            />
            <Button size="sm" onClick={sendDocMsg} disabled={docLoading || !docInput.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-4" data-testid="doc-chat-send">
              {docLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
